import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Hydrant, HydrantStatus, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['FIRE_INSPECTOR', 'CHIEF_FIRE_OFFICER'];
const CHIEF_ROLES = ['CHIEF_FIRE_OFFICER'];

// Same district list m06-fire-rescue validates against (src/service/constants.ts).
const DISTRICTS = [
  'Beau Vallon', 'Anse Royale', 'Baie Lazare', 'Anse Boileau', 'Takamaka', 'Port Glaud', 'Cascade', 'Au Cap',
  'Anse Aux Pins', 'English River', 'Mont Buxton', 'Plaisance', 'St Louis', 'Roche Caiman', 'Bel Air',
  'Mont Fleuri', 'Glacis', 'Bel Ombre', 'Anse Etoile', 'Machabee', 'Pointe Larue', 'Grand Anse Mahe',
  'Grand Anse Praslin', 'Baie Ste Anne',
];

/** Fire & Rescue — the hydrant register (M06): status, last-inspected, and nearest-hydrant lookups feed off this. */
export function Hydrants() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.some((r) => OFFICER_ROLES.includes(r)) : false;
  const isChief = persona ? persona.roles.some((r) => CHIEF_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [district, setDistrict] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (district) params.set('district', district);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m06/hydrants?${params.toString()}`;
  }, [status, district, appliedQ]);

  const listQ = useApiQuery<Page<Hydrant>>(path);

  return (
    <div>
      <PageHeader
        title="Hydrants"
        subtitle="Fire hydrant register — status, last inspected, and nearest-hydrant lookups (M06)."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-40" placeholder="Hydrant code…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={district} onChange={(e) => setDistrict(e.target.value)}>
              <option value="">All districts</option>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="OUT_OF_SERVICE">Out of service</option>
              <option value="DAMAGED">Damaged</option>
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
            {isOfficer ? (
              <button type="button" className="btn-secondary" onClick={() => setShowCreate(true)}>
                + Register
              </button>
            ) : null}
          </form>
        }
      />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No hydrants match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">District</th>
                <th className="table-head">Last inspected</th>
                <th className="table-head">Notes</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((h) => (
                <tr key={h.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(h.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{h.code}</td>
                  <td className="table-cell">{h.district}</td>
                  <td className="table-cell text-xs text-slate-500">{h.lastInspectedAt ? formatDate(h.lastInspectedAt) : 'Never'}</td>
                  <td className="table-cell text-xs text-slate-500">{h.notes ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={h.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <HydrantDetailModal
          id={selectedId}
          canAct={isOfficer}
          canDecommission={isChief}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showCreate ? (
        <CreateHydrantModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function HydrantDetailModal({
  id,
  canAct,
  canDecommission,
  onClose,
  onChanged,
}: {
  id: string;
  canAct: boolean;
  canDecommission: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useApiQuery<Hydrant>(`/api/v1/m06/hydrants/${id}`);
  const h = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const setHydrantStatus = (newStatus: HydrantStatus) => call(() => api.patch(`/api/v1/m06/hydrants/${id}`, { status: newStatus }));
  const markInspected = () => call(() => api.patch(`/api/v1/m06/hydrants/${id}`, { markInspectedNow: true }));
  const saveNotes = () => call(() => api.patch(`/api/v1/m06/hydrants/${id}`, { notes }));
  const decommission = () => call(() => api.del(`/api/v1/m06/hydrants/${id}`));

  return (
    <Modal title={h ? `Hydrant ${h.code}` : 'Hydrant'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {h ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={h.status} />} />
            <Detail label="District" value={h.district} />
            <Detail label="Coordinates" value={`${h.geometry.coordinates[1]}, ${h.geometry.coordinates[0]}`} mono />
            <Detail label="Last inspected" value={h.lastInspectedAt ? formatDate(h.lastInspectedAt) : 'Never'} />
          </div>

          {canAct ? (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <div className="flex flex-wrap gap-2">
                <button className="btn-secondary" disabled={busy || h.status === 'ACTIVE'} onClick={() => setHydrantStatus('ACTIVE')}>
                  Mark active
                </button>
                <button className="btn-secondary" disabled={busy || h.status === 'OUT_OF_SERVICE'} onClick={() => setHydrantStatus('OUT_OF_SERVICE')}>
                  Mark out of service
                </button>
                <button className="btn-secondary" disabled={busy || h.status === 'DAMAGED'} onClick={() => setHydrantStatus('DAMAGED')}>
                  Mark damaged
                </button>
                <button className="btn-primary" disabled={busy} onClick={markInspected}>
                  Mark inspected now
                </button>
              </div>
              <Field label="Notes">
                <div className="flex gap-2">
                  <input className="field-input" defaultValue={h.notes ?? ''} onChange={(e) => setNotes(e.target.value)} />
                  <button className="btn-secondary" disabled={busy} onClick={saveNotes}>
                    Save
                  </button>
                </div>
              </Field>
              {canDecommission ? (
                <button className="btn-danger" disabled={busy} onClick={decommission}>
                  Decommission (soft-delete)
                </button>
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={OFFICER_ROLES} />
          )}
        </div>
      ) : null}
    </Modal>
  );
}

function CreateHydrantModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [lat, setLat] = useState('-4.62');
  const [lng, setLng] = useState('55.45');
  const [district, setDistrict] = useState(DISTRICTS[0]);
  const [parcelNo, setParcelNo] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m06/hydrants', {
        code: code.trim(),
        lat: Number.parseFloat(lat),
        lng: Number.parseFloat(lng),
        district,
        ...(parcelNo.trim() ? { parcelNo: parcelNo.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register a hydrant" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Code">
          <input className="field-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. VIC-HYD-014" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude">
            <input className="field-input" type="number" step="0.0001" value={lat} onChange={(e) => setLat(e.target.value)} />
          </Field>
          <Field label="Longitude">
            <input className="field-input" type="number" step="0.0001" value={lng} onChange={(e) => setLng(e.target.value)} />
          </Field>
        </div>
        <Field label="District">
          <select className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)}>
            {DISTRICTS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Parcel no. (optional)">
          <input className="field-input" value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} />
        </Field>
        <Field label="Notes (optional)">
          <input className="field-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || code.trim().length < 2} onClick={submit}>
            {busy ? 'Registering…' : 'Register hydrant'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
